/**
 * WhatsNewButton - "What's New" spotlight button + modal
 *
 * Displays a sparkle icon in the TopBar. When clicked, opens a glassmorphism
 * modal showing recent feature announcements as compact cards.
 *
 * A teal dot badge pulses when there are unseen features (tracked via
 * localStorage `whatsNewLastSeen` version string).
 *
 * Used in:
 * - Desktop: ImmersiveTopBar (simulation) + LiveTopBar
 * - Mobile: TopBar.mobile.tsx
 *
 * @example
 * <WhatsNewButton variant="topbar" />
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Sparkles,
  Brain,
  Zap,
  PieChart,
  type LucideIcon,
} from 'lucide-react';
import {
  HERO_ACCENT,
  HERO_GLOW_BORDER,
  HERO_GLOW_SHADOW,
} from '@/shared/styles/brandPalette';

// Reuse scanner-myagents-btn class (same as FeedbackButton)
import '@/shared/components/platform/components/scanner/scanner-panel.css';
import { trackEvent } from '@/shared/utils/analytics';

// ============================================================================
// FEATURE DATA
// ============================================================================

interface WhatsNewItem {
  id: string;
  date: string; // YYYY-MM-DD
  icon: LucideIcon;
  title: string;
  titleAr?: string;
  description: string;
  descriptionAr?: string;
}

const WHATS_NEW_ITEMS: WhatsNewItem[] = [
  {
    id: 'portfolio-builder',
    date: '2026-06-24',
    icon: PieChart,
    title: 'Build Your Portfolio in Minutes',
    titleAr: 'ابنِ محفظتك في دقائق',
    description:
      "Set up a paper-trading portfolio fast: pick a ready-made preset, build your own in a few taps, or import your real holdings from a broker CSV (Fidelity, Schwab, IBKR, Binance…). Adjust allocations, set your starting balance, and you're live.",
    descriptionAr:
      'جهّز محفظة تداول تجريبية بسرعة: اختر قالبًا جاهزًا، أو ابنِ محفظتك بنفسك بلمسات قليلة، أو استورد حيازاتك الحقيقية من ملف CSV من وسيطك (Fidelity وSchwab وIBKR وBinance…). عدّل التوزيعات، وحدّد رصيدك الافتتاحي، وانطلق.',
  },
  {
    id: 'preferences-v2',
    date: '2026-02-27',
    icon: Brain,
    title: 'MiDash Now Knows You',
    titleAr: 'MiDash يقرأ أسلوبك ويفهم احتياجاتك',
    description:
      'MiDash learns how you trade, what you follow, and how you like your answers — automatically. The more you use it, the more personal it gets.',
    descriptionAr:
      'يتعلم MiDash تلقائياً طريقة تداولك، والأصول التي تتابعها، والصيغة التي تفضلها في تلقي الإجابات. كلما زاد استخدامك للمنصة، تطورت لتصبح مخصصة لك بالكامل وكأنها صُممت لأجلك أنت فقط.',
  },
];

// Latest feature date — used to detect unseen items
const LATEST_VERSION = WHATS_NEW_ITEMS.reduce(
  (max, item) => (item.date > max ? item.date : max),
  ''
);

const LS_KEY = 'whatsNewLastSeen';

// ============================================================================
// TYPES
// ============================================================================

interface WhatsNewButtonProps {
  variant?: 'topbar' | 'mobile';
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function WhatsNewButton({
  variant = 'topbar',
  className = '',
}: WhatsNewButtonProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Badge: show dot when there are unseen features
  const [hasUnseen, setHasUnseen] = useState(() => {
    const lastSeen = localStorage.getItem(LS_KEY);
    return !lastSeen || lastSeen < LATEST_VERSION;
  });

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [showModal]);

  const handleOpen = useCallback(() => {
    trackEvent('whats_new_opened', {});
    setShowModal(true);
    // Mark as seen
    localStorage.setItem(LS_KEY, LATEST_VERSION);
    setHasUnseen(false);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showModal, handleClose]);

  // Which items are "new" (unseen before this session opened the modal)
  const lastSeenRef = useRef(localStorage.getItem(LS_KEY) || '');

  // ══════════════════════════════════════════════════════════════════════════
  // MODAL
  // ══════════════════════════════════════════════════════════════════════════

  const modal = showModal
    ? createPortal(
        <div
          className={`fixed inset-0 ${isMobile ? 'p-0' : 'flex items-center justify-center p-4'}`}
          style={{
            backgroundColor: isMobile ? 'transparent' : 'rgba(0, 0, 0, 0.85)',
            zIndex: 99999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isMobile) handleClose();
          }}
        >
          <div
            ref={modalRef}
            className={`
              relative overflow-hidden
              ${isMobile
                ? 'w-full h-full flex flex-col'
                : 'w-full max-w-[400px] rounded-2xl animate-modal-enter'
              }
            `}
            style={{
              background:
                'linear-gradient(135deg, rgba(15, 25, 35, 0.98), rgba(8, 18, 28, 0.98))',
              border: isMobile ? 'none' : `1px solid ${HERO_GLOW_BORDER}`,
              boxShadow: isMobile
                ? 'none'
                : `0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 60px ${HERO_GLOW_SHADOW}`,
              maxHeight: isMobile ? '100%' : '80vh',
            }}
          >
            <div
              className={`
                overflow-y-auto flex flex-col
                ${isMobile ? 'flex-1 p-5 pt-[max(20px,env(safe-area-inset-top))]' : 'p-6'}
              `}
              style={isMobile ? {} : { maxHeight: '80vh' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[0.65rem] uppercase tracking-[0.15em] font-medium"
                  style={{
                    background: 'rgba(91, 201, 193, 0.12)',
                    color: HERO_ACCENT,
                    border: `1px solid ${HERO_GLOW_BORDER}`,
                  }}
                >
                  <Sparkles size={14} />
                  {t('whatsNew.badge', "What's New")}
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                  aria-label={t('common.close', 'Close')}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Title */}
              <div className="mb-5">
                <h2 className="text-xl font-bold text-white leading-tight mb-1">
                  {isArabic ? 'الجديد في MiDash' : "What's New in MiDash"}
                </h2>
                <p className="text-white/50 text-sm">
                  {isArabic
                    ? 'آخر التحديثات والميزات'
                    : 'Latest updates and features'}
                </p>
              </div>

              {/* Feature Cards */}
              <div className="flex flex-col gap-3">
                {WHATS_NEW_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isNew = item.date > lastSeenRef.current;

                  return (
                    <div
                      key={item.id}
                      className="relative rounded-xl p-4"
                      style={{
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: `1px solid rgba(255, 255, 255, 0.08)`,
                      }}
                    >
                      {/* NEW badge */}
                      {isNew && (
                        <span
                          className="absolute top-3 text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            [isArabic ? 'left' : 'right']: '12px',
                            background: 'rgba(91, 201, 193, 0.15)',
                            color: HERO_ACCENT,
                            border: `1px solid ${HERO_GLOW_BORDER}`,
                          }}
                        >
                          {isArabic ? 'جديد' : 'NEW'}
                        </span>
                      )}

                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div
                          className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
                          style={{
                            background: 'rgba(91, 201, 193, 0.12)',
                            color: HERO_ACCENT,
                          }}
                        >
                          <Icon size={18} />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-white mb-1 leading-snug">
                            {isArabic && item.titleAr ? item.titleAr : item.title}
                          </h3>
                          <p className="text-xs text-white/55 leading-relaxed">
                            {isArabic && item.descriptionAr
                              ? item.descriptionAr
                              : item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white/30 text-xs text-center">
                  {isArabic
                    ? 'نعمل باستمرار على تحسين MiDash'
                    : "We're constantly improving MiDash"}
                </p>
              </div>

              {/* Mobile safe area */}
              {isMobile && (
                <div style={{ height: 'max(16px, env(safe-area-inset-bottom))' }} />
              )}
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <>
      <button
        type="button"
        className={`scanner-myagents-btn ${hasUnseen ? 'feedback-pulse' : ''} ${className}`}
        onClick={handleOpen}
        aria-label={t('whatsNew.button', "What's New")}
        style={{ position: 'relative' }}
      >
        <Sparkles size={14} />

        {/* Unseen dot badge */}
        {hasUnseen && (
          <span
            style={{
              position: 'absolute',
              top: variant === 'mobile' ? '-1px' : '2px',
              right: variant === 'mobile' ? '-1px' : '2px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: HERO_ACCENT,
              boxShadow: `0 0 6px ${HERO_ACCENT}`,
            }}
          />
        )}
      </button>

      {modal}
    </>
  );
}

export default WhatsNewButton;
